/**
 * Payroll Paystub PDF Service
 * Generates professional paystub PDFs using jsPDF.
 * Follows the same patterns as career-pdf-service.ts / car-pdf-service.ts.
 */

import jsPDF from 'jspdf';
import { PayrollResponse } from './payroll-api';

export interface PaystubPDFOptions {
  employeeName: string;
  payPeriodLabel: string;   // e.g. "March 1 – March 15, 2026"
  payDate: string;          // e.g. "2026-03-15"
  postTaxDeductions: {
    lifeInsurance: number;
    healthDental: number;
    other: number;
    otherLabel: string;
  };
}

export class PayrollPDFService {
  private readonly MARGIN = 25.4; // 1 inch in mm
  private readonly PAGE_WIDTH = 210; // A4 width
  private readonly PAGE_HEIGHT = 297; // A4 height

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  private formatPercent(value: number): string {
    return value.toFixed(2) + '%';
  }

  /** Red header bar with branding (matches other PDF services) */
  private addHeader(pdf: jsPDF, subtitle: string): number {
    const margin = this.MARGIN;
    const headerHeight = 20;

    // Red background
    pdf.setFillColor(220, 38, 38); // #dc2626
    pdf.rect(0, 0, this.PAGE_WIDTH, headerHeight, 'F');

    // Company name
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont('times', 'bold');
    pdf.text("Larry's Help", margin, 12);

    // Website
    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.text('LarrysHelp.org', this.PAGE_WIDTH - margin, 12, { align: 'right' });

    // Reset text color
    pdf.setTextColor(0, 0, 0);

    // Subtitle
    let yPos = headerHeight + 12;
    pdf.setFontSize(16);
    pdf.setFont('times', 'bold');
    pdf.text('Employee Pay Stub', margin, yPos);
    yPos += 7;

    pdf.setFontSize(10);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(60, 60, 60);
    pdf.text(subtitle, margin, yPos);

    // Underline
    pdf.setDrawColor(220, 38, 38);
    pdf.setLineWidth(0.3);
    pdf.line(margin, yPos + 2, this.PAGE_WIDTH - margin, yPos + 2);

    pdf.setTextColor(0, 0, 0);
    return yPos + 10;
  }

  /** Footer with page info */
  private addFooter(pdf: jsPDF, reportDate: string): void {
    const margin = this.MARGIN;
    const y = this.PAGE_HEIGHT - margin + 5;

    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, this.PAGE_WIDTH - margin, y);

    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text("Prepared by Larry's Help", margin, y + 5);
    pdf.text(reportDate, this.PAGE_WIDTH / 2, y + 5, { align: 'center' });
    pdf.text('Page 1 of 1', this.PAGE_WIDTH - margin, y + 5, { align: 'right' });
  }

  /** Section heading with underline */
  private addSectionTitle(pdf: jsPDF, title: string, y: number): number {
    const margin = this.MARGIN;
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('times', 'bold');
    pdf.text(title, margin, y);

    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    const textWidth = pdf.getTextWidth(title);
    pdf.line(margin, y + 1, margin + textWidth, y + 1);

    pdf.setFont('times', 'normal');
    return y + 8;
  }

  /** Key-value row: label on left, value right-aligned */
  private addRow(pdf: jsPDF, label: string, value: string, y: number, options?: {
    bold?: boolean;
    indent?: number;
    color?: string;
    topBorder?: boolean;
    bottomBorder?: boolean;
  }): number {
    const margin = this.MARGIN + (options?.indent || 0);
    const valueX = this.PAGE_WIDTH - this.MARGIN - 5;

    if (options?.topBorder) {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.3);
      pdf.line(this.MARGIN, y - 2, this.PAGE_WIDTH - this.MARGIN, y - 2);
    }

    pdf.setFontSize(10);
    pdf.setFont('times', options?.bold ? 'bold' : 'normal');

    // Label
    pdf.setTextColor(0, 0, 0);
    pdf.text(label, margin, y);

    // Value with optional color
    if (options?.color === 'green') {
      pdf.setTextColor(5, 150, 105); // #059669
    } else if (options?.color === 'red') {
      pdf.setTextColor(220, 38, 38); // #dc2626
    } else {
      pdf.setTextColor(0, 0, 0);
    }
    pdf.text(value, valueX, y, { align: 'right' });

    pdf.setTextColor(0, 0, 0);

    if (options?.bottomBorder) {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(this.MARGIN, y + 2, this.PAGE_WIDTH - this.MARGIN, y + 2);
    }

    return y + 5;
  }

  /** Main PDF generation */
  generatePaystubPDF(result: PayrollResponse, options: PaystubPDFOptions): void {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const reportDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const pensionLabel = result.pension_label || 'CPP';
      const isQuebec = result.rates_info.is_quebec;
      const pp = result.per_period;
      const annual = result.annual;
      const freq = result.pay_frequency;
      const freqLabel = freq === 'bi-weekly' ? 'Bi-Weekly'
        : freq === 'semi-monthly' ? 'Semi-Monthly'
        : freq.charAt(0).toUpperCase() + freq.slice(1);

      const postTax = options.postTaxDeductions;
      const postTaxTotal = (postTax.lifeInsurance || 0) + (postTax.healthDental || 0) + (postTax.other || 0);
      const takeHome = pp.net_pay - postTaxTotal;

      // Header
      const subtitle = options.employeeName + ' | ' + freqLabel + ' | ' + result.province;
      let y = this.addHeader(pdf, subtitle);

      // Employee info block
      y += 2;
      pdf.setFontSize(10);
      pdf.setFont('times', 'normal');
      pdf.text('Employee: ' + options.employeeName, this.MARGIN, y);
      pdf.text('Pay Date: ' + options.payDate, this.PAGE_WIDTH - this.MARGIN - 5, y, { align: 'right' });
      y += 5;
      pdf.text('Pay Period: ' + options.payPeriodLabel, this.MARGIN, y);
      pdf.text('Frequency: ' + freqLabel + ' (' + result.periods_per_year + ' periods/year)', this.PAGE_WIDTH - this.MARGIN - 5, y, { align: 'right' });
      y += 5;
      pdf.text('Province: ' + result.province, this.MARGIN, y);
      pdf.text('Tax Year: ' + result.year, this.PAGE_WIDTH - this.MARGIN - 5, y, { align: 'right' });
      y += 10;

      // ---- EARNINGS ----
      y = this.addSectionTitle(pdf, 'EARNINGS', y);

      if (result.hourly_details) {
        y = this.addRow(pdf, 'Regular Hours (' + result.hourly_details.regular_hours + 'h x ' + this.formatCurrency(result.hourly_details.hourly_rate) + '/hr)', this.formatCurrency(result.hourly_details.regular_pay_per_period), y);
        if (result.hourly_details.overtime_hours_per_week > 0) {
          y = this.addRow(pdf, 'Overtime Hours (' + result.hourly_details.overtime_hours_per_week.toFixed(1) + 'h x ' + result.hourly_details.overtime_rate_multiplier + 'x)', this.formatCurrency(result.hourly_details.overtime_pay_per_period), y);
        }
      }

      y = this.addRow(pdf, 'Gross Pay', this.formatCurrency(pp.gross_pay), y, { bold: true, color: 'green', topBorder: true });
      y += 3;

      // ---- PRE-TAX DEDUCTIONS ----
      if (pp.total_pre_tax_deductions && pp.total_pre_tax_deductions > 0) {
        y = this.addSectionTitle(pdf, 'PRE-TAX DEDUCTIONS', y);
        if (pp.rrsp_deduction && pp.rrsp_deduction > 0) {
          y = this.addRow(pdf, 'RRSP Contribution', this.formatCurrency(pp.rrsp_deduction), y, { color: 'red' });
        }
        if (pp.pension_plan_deduction && pp.pension_plan_deduction > 0) {
          y = this.addRow(pdf, 'Pension Plan (RPP)', this.formatCurrency(pp.pension_plan_deduction), y, { color: 'red' });
        }
        if (pp.union_dues_deduction && pp.union_dues_deduction > 0) {
          y = this.addRow(pdf, 'Union Dues', this.formatCurrency(pp.union_dues_deduction), y, { color: 'red' });
        }
        y = this.addRow(pdf, 'Total Pre-Tax Deductions', this.formatCurrency(pp.total_pre_tax_deductions), y, { bold: true, topBorder: true, color: 'red' });
        y += 3;
      }

      // ---- STATUTORY DEDUCTIONS ----
      y = this.addSectionTitle(pdf, 'STATUTORY DEDUCTIONS', y);
      y = this.addRow(pdf, 'Federal Income Tax', this.formatCurrency(pp.federal_tax), y, { color: 'red' });
      y = this.addRow(pdf, 'Provincial Income Tax', this.formatCurrency(pp.provincial_tax), y, { color: 'red' });
      y = this.addRow(pdf, pensionLabel, this.formatCurrency(pp.pension_employee), y, { color: 'red' });
      if (pp.pension2_employee > 0) {
        y = this.addRow(pdf, pensionLabel + '2', this.formatCurrency(pp.pension2_employee), y, { color: 'red' });
      }
      y = this.addRow(pdf, 'Employment Insurance (EI)', this.formatCurrency(pp.ei_employee), y, { color: 'red' });
      if (isQuebec && pp.qpip_employee > 0) {
        y = this.addRow(pdf, 'QPIP', this.formatCurrency(pp.qpip_employee), y, { color: 'red' });
      }
      y = this.addRow(pdf, 'Total Statutory Deductions', this.formatCurrency(pp.total_deductions), y, { bold: true, topBorder: true, color: 'red' });
      y += 3;

      // ---- POST-TAX DEDUCTIONS ----
      if (postTaxTotal > 0) {
        y = this.addSectionTitle(pdf, 'POST-TAX DEDUCTIONS', y);
        if (postTax.lifeInsurance > 0) {
          y = this.addRow(pdf, 'Group Life Insurance', this.formatCurrency(postTax.lifeInsurance), y, { color: 'red' });
        }
        if (postTax.healthDental > 0) {
          y = this.addRow(pdf, 'Health / Dental Benefits', this.formatCurrency(postTax.healthDental), y, { color: 'red' });
        }
        if (postTax.other > 0) {
          y = this.addRow(pdf, postTax.otherLabel || 'Other', this.formatCurrency(postTax.other), y, { color: 'red' });
        }
        y = this.addRow(pdf, 'Total Post-Tax Deductions', this.formatCurrency(postTaxTotal), y, { bold: true, topBorder: true, color: 'red' });
        y += 3;
      }

      // ---- NET PAY ----
      y += 2;
      // Double line above net pay
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(this.MARGIN, y, this.PAGE_WIDTH - this.MARGIN, y);
      pdf.line(this.MARGIN, y + 1.5, this.PAGE_WIDTH - this.MARGIN, y + 1.5);
      y += 6;

      pdf.setFontSize(14);
      pdf.setFont('times', 'bold');
      pdf.text('NET PAY', this.MARGIN, y);
      pdf.setTextColor(5, 150, 105);
      pdf.text(this.formatCurrency(takeHome), this.PAGE_WIDTH - this.MARGIN - 5, y, { align: 'right' });
      pdf.setTextColor(0, 0, 0);
      y += 10;

      // ---- ANNUAL SUMMARY ----
      y = this.addSectionTitle(pdf, 'ANNUAL SUMMARY (Year-to-Date)', y);
      y = this.addRow(pdf, 'Gross Annual Salary', this.formatCurrency(annual.gross_salary), y, { color: 'green' });
      y = this.addRow(pdf, 'Total Annual Deductions', this.formatCurrency(annual.total_employee_deductions), y, { color: 'red' });
      y = this.addRow(pdf, 'Annual Net Pay', this.formatCurrency(annual.net_pay), y, { bold: true, color: 'green' });
      y += 3;

      // ---- EMPLOYER CONTRIBUTIONS ----
      y = this.addSectionTitle(pdf, 'EMPLOYER CONTRIBUTIONS (per period)', y);
      y = this.addRow(pdf, pensionLabel + ' (Employer Match)', this.formatCurrency(pp.pension_employer), y);
      if (pp.pension2_employer > 0) {
        y = this.addRow(pdf, pensionLabel + '2 (Employer Match)', this.formatCurrency(pp.pension2_employer), y);
      }
      y = this.addRow(pdf, 'EI (Employer)' + (isQuebec ? ' + QPIP' : ''), this.formatCurrency(pp.ei_employer + (isQuebec ? pp.qpip_employer : 0)), y);
      y = this.addRow(pdf, 'Total Employer Contributions', this.formatCurrency(pp.total_employer_contributions), y, { bold: true, topBorder: true });
      y += 3;

      // ---- EFFECTIVE RATES ----
      y = this.addSectionTitle(pdf, 'EFFECTIVE TAX RATES', y);
      y = this.addRow(pdf, 'Federal Tax Rate', this.formatPercent(result.effective_rates.federal_rate), y);
      y = this.addRow(pdf, 'Provincial Tax Rate', this.formatPercent(result.effective_rates.provincial_rate), y);
      y = this.addRow(pdf, pensionLabel + ' Rate', this.formatPercent(result.effective_rates.pension_rate), y);
      y = this.addRow(pdf, 'EI Rate', this.formatPercent(result.effective_rates.ei_rate), y);
      y = this.addRow(pdf, 'Total Deduction Rate', this.formatPercent(result.effective_rates.total_tax_rate), y, { bold: true, topBorder: true });

      // ---- DISCLAIMER ----
      y += 8;
      pdf.setFontSize(8);
      pdf.setFont('times', 'italic');
      pdf.setTextColor(100, 100, 100);
      const disclaimer = "This pay stub is an estimate based on published CRA rates and T4127 formulas. " +
        "It is for informational purposes only and should not be relied upon as a substitute for " +
        "professional payroll services. The employer is responsible for verifying all deductions " +
        "and remittances with CRA. Larry's Help assumes no liability for payroll calculation errors.";
      const lines = pdf.splitTextToSize(disclaimer, this.PAGE_WIDTH - 2 * this.MARGIN);
      lines.forEach((line: string) => {
        pdf.text(line, this.MARGIN, y);
        y += 4;
      });

      // Footer
      this.addFooter(pdf, reportDate);

      // Download
      const safeName = options.employeeName.replace(/[^a-zA-Z0-9]/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = 'Paystub_' + safeName + '_' + dateStr + '.pdf';
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating paystub PDF:', error);
    }
  }
}

const payrollPDFService = new PayrollPDFService();
export default payrollPDFService;
